import type { HeaderPageKey } from "../header/Headerpages";

/** Every app the global search knows about. */
export type SearchAppId =
  | "hospitality"
  | "accounting"
  | "crm"
  | "retail"
  | "finance"
  | "task"
  | "calltracker"
  | "email"
  | "chat"
  | "calendar"
  | "settings";

export interface AppMeta {
  id: SearchAppId;
  label: string;
  /** accent used for the app badge on results */
  color: string;
  /** the Home tab that opens this app ("calltracker" and "chat" open a panel instead) */
  tab: string;
}

export const SEARCH_APPS: AppMeta[] = [
  { id: "hospitality", label: "Hospitality", color: "#0f6cbd", tab: "hospitality" },
  { id: "accounting", label: "Accounting", color: "#1e7a3c", tab: "Accounting" },
  { id: "crm", label: "CRM", color: "#7a3fb5", tab: "CRM" },
  { id: "retail", label: "Retail & POS", color: "#c2570c", tab: "Retail" },
  { id: "finance", label: "Finance & Operations", color: "#0b6a6a", tab: "Finance" },
  { id: "task", label: "Tasks", color: "#b8791a", tab: "Task" },
  { id: "calltracker", label: "Call tracker", color: "#c4314b", tab: "calltracker" },
  { id: "email", label: "Email", color: "#2b5fd9", tab: "email" },
  { id: "chat", label: "Chats", color: "#6b4fbb", tab: "chat" },
  { id: "calendar", label: "Calendar", color: "#0a7f9e", tab: "Calender" },
  { id: "settings", label: "Settings & help", color: "#5b6472", tab: "settings" },
];

export const appMeta = (id: SearchAppId): AppMeta =>
  SEARCH_APPS.find((a) => a.id === id) ?? SEARCH_APPS[SEARCH_APPS.length - 1];

/** Where Home should go when a result is opened. */
export interface SearchTarget {
  tab: string;
  /** a Settings (Setup) screen, e.g. "AccessRoles" or "Crm:admin.pipelines" */
  settupValue?: string;
  /** one of the header pages (help, account, notifications ...) */
  headerPage?: HeaderPageKey;
}

/** What a provider returns: a raw record plus where it lives. Scoring happens centrally. */
export interface SearchHit {
  /** unique within the app and kind */
  id: string;
  app: SearchAppId;
  /** "Room", "Customer", "Setup screen" ... */
  kind: string;
  record: Record<string, unknown>;
  target: SearchTarget;
  /** the server already matched this itself; keep it even if our own scorer is unsure */
  trusted?: boolean;
}

export interface SearchResult {
  key: string;
  app: SearchAppId;
  kind: string;
  title: string;
  subtitle: string;
  score: number;
  target: SearchTarget;
  /** every readable field of the record, for the detail panel */
  fields: [string, string][];
}

export type AppState = "idle" | "loading" | "done" | "error" | "unavailable";

export interface AppStatus {
  state: AppState;
  count: number;
  error?: string;
}

export interface SearchCtx {
  token: string;
  currentApp: SearchAppId | null;
}

export interface SearchProvider {
  app: SearchAppId;
  label: string;
  search: (q: string, ctx: SearchCtx) => Promise<SearchHit[]>;
}